import {
  AfterContentInit,
  booleanAttribute,
  computed,
  Directive,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, ValidationErrors, Validator } from '@angular/forms';
import { MibMoment, MibWeekday } from '../../foundations';
import { MibDatepickerPanelComponent } from './date-picker-panel/date-picker-panel.component';
import { MibFieldControl } from '../../internal/field-control.utils';
import { applyMask } from '../../internal/input-mask.utils';
import { startOfDay } from '../../internal/dates.utils';
import { dayOf, maskDate } from './picker.utils';

/* `[formField]` writes `undefined` and types the bounds after the field's value (a range here): both mean no bound. */
function momentOnly(at: unknown): MibMoment | null {
  return typeof at === 'string' || typeof at === 'number' || at instanceof Date ? at : null;
}

/** What the date picker and the date range picker share: their bounds, their labels and their panel. */
@Directive()
export abstract class MibDatepickerBase<V> implements ControlValueAccessor, Validator, AfterContentInit {
  /** The first day that can be picked, as a moment; a date before it raises the `Min` error. */
  readonly min = input<MibMoment | null, MibMoment | V | null | undefined>(null, { transform: momentOnly });
  /** The last day that can be picked, as a moment; a date after it raises the `Max` error. */
  readonly max = input<MibMoment | null, MibMoment | V | null | undefined>(null, { transform: momentOnly });
  /** A BCP 47 tag for the order a date is typed in and the names of the months; the browser's by default. */
  readonly locale = input<string | undefined>(undefined);
  /** The first day of the week, as `Date.getDay()` numbers it: 0 is Sunday. */
  readonly weekStartsOn = input<MibWeekday>(1);
  /** Takes digits only and writes the separators of the locale as they are typed; `false` reads any text. */
  readonly mask = input(true, { transform: booleanAttribute });
  /** Opens the panel on a click in the field, as the calendar button does; `false` leaves it to the button. */
  readonly openOnClick = input(true, { transform: booleanAttribute });
  /** The `id` of the (first) text field, for a `<label for>`. */
  readonly inputId = input<string | undefined>(undefined);
  /** Accessible name of the calendar button. */
  readonly toggleLabel = input('Open calendar');
  /** Accessible name of the previous-month button. */
  readonly previousLabel = input('Previous month');
  /** Accessible name of the next-month button. */
  readonly nextLabel = input('Next month');

  /** The value once the reader settles on it: Enter in the field, or a pick in the panel. */
  readonly confirmed = output<V>();

  protected readonly control = new MibFieldControl();
  protected readonly disabled = signal(false);
  protected readonly open = signal(false);
  protected readonly month = signal(startOfDay(new Date()));

  protected readonly minDay = computed(() => dayOf(this.min()));
  protected readonly maxDay = computed(() => dayOf(this.max()));

  private readonly origin = viewChild.required<ElementRef<HTMLElement>>('origin');
  private readonly toggle = viewChild.required<ElementRef<HTMLButtonElement>>('toggle');
  private readonly panel = viewChild(MibDatepickerPanelComponent);
  protected readonly originElement = computed(() => this.origin().nativeElement);

  private focusPanel = true;
  protected onChange: (value: V) => void = () => undefined;
  protected onTouched: () => void = () => undefined;
  protected onValidatorChange: () => void = () => undefined;

  /** Writes a value set by the form into the field. Part of `ControlValueAccessor`. */
  abstract writeValue(value: unknown): void;
  /** The errors of the current text and value. Part of `Validator`. */
  abstract validate(control: AbstractControl): ValidationErrors | null;
  /** The day the panel opens on. */
  protected abstract openingDay(): Date | null;

  /** `formControlName` sets its control after this component's `ngOnInit`, so the link waits for it. */
  ngAfterContentInit(): void {
    this.control.connect();
  }

  /** Takes the callback the form calls on a change. Part of `ControlValueAccessor`. */
  registerOnChange(fn: (value: V) => void): void {
    this.onChange = fn;
  }

  /** Takes the callback the form calls on leaving the field. Part of `ControlValueAccessor`. */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Takes the callback the form calls when the bounds change. Part of `Validator`. */
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  /** Disables the field and closes the panel. Part of `ControlValueAccessor`. */
  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
    if (disabled) this.open.set(false);
  }

  /** From the button or the keyboard the focus goes into the month; from a click in the field it stays there, to type. */
  protected toggleOpen(focusPanel = true): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.focusPanel = focusPanel;
    this.month.set(this.openingDay() ?? this.minDay() ?? startOfDay(new Date()));
    this.open.set(true);
  }

  protected onFieldClick(): void {
    if (this.openOnClick() && !this.open() && !this.disabled()) this.toggleOpen(false);
  }

  protected onAttach(): void {
    if (this.focusPanel) setTimeout(() => this.panel()?.focus());
  }

  /* Escape gives the focus back to the button only when it was in the panel. */
  protected onEscape(): void {
    this.close(!this.originElement().contains(document.activeElement));
  }

  /** A complete date typed while the panel is open brings the panel to its month. */
  protected follow(day: Date | null): void {
    if (day && this.open()) this.month.set(day);
  }

  protected close(returnFocus = true): void {
    if (!this.open()) return;
    this.open.set(false);
    this.control.refresh();
    if (returnFocus) this.toggle().nativeElement.focus();
  }

  protected onOutsideClick(event: MouseEvent): void {
    if (this.originElement().contains(event.target as Node)) return;
    this.close(false);
  }

  /** The text of the field the reader typed in, rewritten by the mask when there is one. */
  protected readField(event: Event): string {
    const field = event.target as HTMLInputElement;
    return this.mask() ? applyMask(field, event, (text) => maskDate(text, this.locale())) : field.value;
  }

  protected onLeave(): void {
    this.onTouched();
    this.control.refresh();
  }
}
